import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  adamIntopQuery,
  companyContactsQuery,
  companyEventsQuery,
  companyGroupsQuery,
  companyInformationQuery,
  companyOwnersQuery,
  companyShellQuery,
  isCompanyTab,
  removeGroup,
  removeOwner,
  saveAdamIntop,
  saveContactRole,
  saveGroup,
  saveSupport,
  uploadCompanyImage,
  type CompaniesTableRequest,
  type CompanyTab,
  type InformationTab,
} from '../../api/settingsCompanies'
import { dayMonthYear, stamp } from '../../inventoree/format'
import { DataTable, useTableState, type TableColumn, type TableState } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { InnerTabs } from '../../ui/InnerTabs'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Companies, sectiunea COMPANY SETTINGS:
 * `version-3/settings/companies/my_company/tabList.php` (bara) plus cele sase
 * ecrane ale ei - `my_company.php`, `contacts.php`, `groups.php`,
 * `owners.php`, `events.php`, `adam_intop_companies.php` - element cu element.
 *
 * TREI NIVELURI DE MENIU
 * ----------------------
 * 1. BARA din stanga, cu capul "COMPANY SETTINGS" (bara are trei capete; asta
 *    e primul). Titlul se deseneaza cand `group` se schimba, ca la General, ca
 *    ecranul sa nu stie nimic despre ce sectiuni exista si sa poata primi
 *    celelalte doua fara sa se atinga.
 * 2. `ul.nav-tabs` DINAUNTRUL a doua file: "Information" are sase
 *    (Company logo, Agent logo, Agent icon, EaseeHelp logo, Service Provider,
 *    Support), "Contacts" are trei (Main, Financiar, Administrative). Fiecare
 *    cu `canAccess`-ul ei in macheta veche; aici vin filtrate de pe server.
 * 3. Sub ele nu mai e nimic: o lista sau un formular.
 *
 * CE E ALTFEL, SI DE CE
 * ---------------------
 *  - CELE CINCI INCARCARI DE IMAGINE au ramas in portal - trei din ele
 *    micsoreaza fisierul cu `Intervention\Image`, o biblioteca pe care v4 nu o
 *    are. Filele lor ARATA imaginea de acum (adresa vine de la server) si duc
 *    la ecranul vechi ca sa se schimbe. Mutate pe jumatate ar fi urcat pe
 *    serverul de descarcari imagini de alta marime decat cele de azi.
 *  - CASUTA "Link URL address" din fila "Agent icon" NU s-a mutat: nu o
 *    citeste niciun capat din portal (`agent_ico()` scrie doar `agent_icon`).
 *    Randul "Current link url:" ramane - aia e o valoare adevarata din baza.
 *  - CELE TREI FORMULARE DE CONTACT sunt unul singur, cu `role`. In portal
 *    erau trei metode identice care asteptau toate campul `main_contact`, chiar
 *    si cand scriau in `financiar_contact`.
 *  - BUTONUL "Remove" DIN "Device Owners" chiar are o poarta pe firma. Vezi
 *    `removeOwner` din api/settingsCompanies.ts.
 *  - Cele trei butoane "Save Changes" din `contacts.php` aveau toate
 *    `id="btn-company-logo"` (acelasi id de trei ori, imprumutat din alta
 *    macheta), iar JS-ul lor scria intr-un `#main-contact-text` care nu exista
 *    in pagina. Nu s-au mutat nici id-urile, nici scrierea in gol.
 */

const GROUPS_TABLE = 'settings-company-groups'

const OWNERS_TABLE = 'settings-company-owners'

const EVENTS_TABLE = 'settings-company-events'

/** `moment(...).format('D MMM YYYY, HH:mm')`, ca in modelele de lista vechi. */
function whenText(value: unknown): string {
  const when = stamp(value)
  if (!when) return ''
  const hh = String(when.getHours()).padStart(2, '0')
  const mm = String(when.getMinutes()).padStart(2, '0')
  return `${dayMonthYear(when)}, ${hh}:${mm}`
}

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** Ce s-a intamplat, spus cu vorbele SERVERULUI cand le are. */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/** Cererea de lista, din starea tabelului. */
function tableRequest(state: TableState, names: string[], fallback: string): CompaniesTableRequest {
  return {
    draw: 1,
    start: state.start,
    length: state.length,
    orderColumn: state.order.column,
    orderName: names[state.order.column] ?? fallback,
    orderDir: state.order.dir,
    search: state.search,
  }
}

export function CompanySettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(companyShellQuery)

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isCompanyTab(tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/companies/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const current = isCompanyTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          {shell.data.tabs.map((item, index) => (
            <SidebarBlock key={item.id} tab={item} current={current} previous={shell.data.tabs[index - 1]} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </nav>
      <div className="page-content-wrapper">
        {current === 'my_company' && <InformationPanel />}
        {current === 'contacts' && <ContactsPanel />}
        {current === 'groups' && <GroupsPanel />}
        {current === 'owners' && <OwnersPanel />}
        {current === 'companyEvents' && <EventsPanel />}
        {current === 'adam_intop_companies' && <AdamIntopPanel />}
      </div>
    </>
  )
}

/** Titlul de sectiune apare o data, la prima fila a grupului; despartitorul inaintea lui. */
function SidebarBlock({ tab, current, previous }: { tab: CompanyTab; current: string; previous?: CompanyTab }) {
  const opens = !previous || previous.group !== tab.group
  return (
    <>
      {opens && previous ? <li className="menu-block-divider"></li> : null}
      {opens ? <li className="menu-header">{tab.group}</li> : null}
      <SidebarTab tab={tab} current={current} />
    </>
  )
}

function SidebarTab({ tab, current }: { tab: CompanyTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/companies/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.id} href={'/app/settings/companies/' + tab.id} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

/*
 * Filele dinauntru (`ul.nav-tabs.tabs-shdw` din machetele vechi, al treilea
 * nivel de meniu) sunt `InnerTabs` din `ui/`, aceleasi ca in My Account si Cost
 * Reports, desenate ca bara de deasupra lor.
 *
 * Prima fila e cea deschisa, ca in portal (`li.active` pe prima). Cand omul
 * n-are dreptul primei, prima e cea dintai pe care o are - lista vine deja
 * filtrata de pe server, deci aici nu se mai intreaba nimic.
 */

/* -------------------------------------------------------------------------
 * Information (my_company.php) - sase file dinauntru
 * ---------------------------------------------------------------------- */

function InformationPanel() {
  const page = useQuery(companyInformationQuery)
  const [current, setCurrent] = useState('')

  useEffect(() => {
    if (!page.data || page.data.tabs.length === 0) return
    setCurrent((prev) => (prev !== '' && page.data.tabs.some((t) => t.id === prev) ? prev : page.data.tabs[0].id))
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const tabs = page.data.tabs
  if (tabs.length === 0) {
    return (
      <div id="content">
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> There is nothing here you may see.
        </div>
      </div>
    )
  }
  const open = tabs.find((t) => t.id === current) ?? tabs[0]

  return (
    <div id="content">
      <InnerTabs tabs={tabs} current={open.id} onPick={setCurrent} />
      <div className="tab-content p-3 overflow-hidden">
        {open.id === 'suport' ? (
          <SupportForm phone={page.data.supportPhone} email={page.data.supportEmail} />
        ) : (
          <ImageTab tab={open} ready={page.data.imagesReady} agentLogoLink={page.data.agentLogoLink} />
        )}
      </div>
    </div>
  )
}

/** Textele de sub fiecare imagine, cuvant cu cuvant din `my_company.php`. */
const IMAGE_HINTS: Record<string, string[]> = {
  company_logo: [
    'Please upload only jpg, jpeg and png file formats.',
    'The image you upload will be resized to a square.',
    'To ensure the quality of the logo, please upload an image at least 130px by 130px.',
  ],
  agent_logo: [
    'Please upload only jpg, jpeg and png file formats.',
    'To ensure the quality of the logo, please upload an image at least 126px by 33px.',
  ],
  agent_icon: ['Please upload only icon file format.'],
  easeehelp_logo: [
    'Please upload only jpg, jpeg and png file formats.',
    'To ensure the quality of the logo, please upload an image at least 1000px by 100px.',
  ],
  service_provider: [
    'Please upload only jpg, jpeg and png file formats.',
    'To ensure the quality of the background, please upload an image with 1920x1080 pixels.',
  ],
}

const IMAGE_WIDTH: Record<string, number> = {
  company_logo: 100,
  agent_logo: 200,
  agent_icon: 100,
  easeehelp_logo: 200,
  service_provider: 200,
}

/**
 * O fila de imagine (`my_company.php`, blocurile `form-company-logo` & co.):
 * butonul "Upload" care deschide selectorul ascuns, imaginea de acum, textul
 * de ajutor si "Save Changes". Incarcarea pleaca la API
 * (`uploadCompanyImage`), care o micsoreaza unde portalul micsora si o pune pe
 * serverul de descarcari; imaginea se reincarca cu adresa noua (`?t=`), ca in
 * `attr('src', url + '?' + Date.now())` din scriptul vechi.
 */
function ImageTab({
  tab,
  ready,
  agentLogoLink,
}: {
  tab: InformationTab
  ready: boolean
  agentLogoLink: string
}) {
  const query = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [refreshed, setRefreshed] = useState(0)
  const inputId = tab.id === 'agent_icon' ? 'agent_ico' : tab.id === 'agent_logo' ? 'optimum_logo' : tab.id === 'easeehelp_logo' ? 'easee_logo' : tab.id === 'service_provider' ? 'easee_bg' : 'company_logo'

  useEffect(() => {
    setFile(null)
  }, [tab.id])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!file) {
      toast('Please choose a file.', 'Error', 'error')
      return
    }
    setBusy(true)
    void uploadCompanyImage(tab.id, file)
      .then(() => {
        setBusy(false)
        setFile(null)
        setRefreshed(Date.now())
        toast('Image saved.', 'Success')
        void query.invalidateQueries({ queryKey: ['settings', 'companies', 'information'] })
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      })
  }

  return (
    <form className="tab-pane fade in active" id={'form-' + tab.id.replace(/_/g, '-')} onSubmit={submit} encType="multipart/form-data">
      <div style={{ border: '1px dashed #aaa', borderRadius: 4, padding: 8, display: 'inline-block' }}>
        <div className="d-flex">
          <div>
            <label className="col-md-12" htmlFor={inputId}>
              Change {tab.label.toLowerCase()}
            </label>
            <div className="btn float-left">
              <label className="btn btn-default upload-btn" htmlFor={inputId} style={{ marginBottom: 0 }}>
                <i className="fas fa-cloud-upload-alt" aria-hidden="true"></i> Upload{file ? ': ' + file.name : ''}
              </label>
              <input
                type="file"
                className="hide"
                name={inputId}
                id={inputId}
                accept={tab.id === 'agent_icon' ? '.ico,image/x-icon' : 'image/*'}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <div style={{ border: '1px solid #aaa', borderRadius: 6, overflow: 'hidden' }}>
            {ready && tab.url !== '' ? (
              <img
                id={'current_' + tab.id}
                style={{ maxWidth: IMAGE_WIDTH[tab.id] ?? 200 }}
                src={refreshed > 0 ? tab.url + (tab.url.includes("?") ? "&" : "?") + "t=" + refreshed : tab.url}
                alt={tab.label}
                onError={(event) => {
                  /* `onerror="this.style.display='none'"` din macheta veche. */
                  event.currentTarget.style.display = 'none'
                }}
              />
            ) : (
              <span className="help-block" style={{ margin: 8 }}>
                No image server configured.
              </span>
            )}
          </div>
        </div>
      </div>
      {tab.id === 'agent_icon' ? (
        <div className="mt-1">
          <label>Current link url:&nbsp;</label>
          <span>{agentLogoLink}</span>
        </div>
      ) : null}
      <div>
        <span className="help-block">
          {(IMAGE_HINTS[tab.id] ?? []).map((line) => (
            <span key={line}>
              {line}
              <br />
            </span>
          ))}
        </span>
        <div>
          <button className="btn btn-success" type="submit" id={'btn-' + inputId.replace(/_/g, '-')} disabled={busy || !ready}>
            Save Changes
          </button>
        </div>
      </div>
    </form>
  )
}

function SupportForm({ phone, email }: { phone: string; email: string }) {
  const query = useQueryClient()
  const [nextPhone, setNextPhone] = useState(phone)
  const [nextEmail, setNextEmail] = useState(email)

  useEffect(() => {
    setNextPhone(phone)
    setNextEmail(email)
  }, [phone, email])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveSupport({ support_phone: nextPhone, support_email: nextEmail })
      .then(() => {
        toast('Support data was saved!', 'Success')
        void query.invalidateQueries({ queryKey: ['settings', 'companies', 'information'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div className="tab-pane fade in active">
      <div className="mb-2">
        <div
          className="px-4 py-5"
          style={{ border: '1px dashed #aaa', borderRadius: 4, display: 'inline-block', height: 124 }}
        >
          <label>Current phone:&nbsp;</label>
          <span id="current_phone">{phone}</span>
          <br />
          <label>Current email:&nbsp;</label>
          <span id="current_email">{email}</span>
        </div>
      </div>
      <div className="row">
        <form method="post" id="suport-form" onSubmit={submit}>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="suport_phone">Support Phone:</label>
              <input
                type="text"
                className="form-control input-sm"
                name="support_phone"
                id="suport_phone"
                value={nextPhone}
                onChange={(e) => setNextPhone(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="suport_email">Support Email:</label>
              <input
                type="text"
                className="form-control input-sm"
                name="support_email"
                id="suport_email"
                value={nextEmail}
                onChange={(e) => setNextEmail(e.target.value)}
              />
            </div>
            <div>
              <button className="btn btn-success mt-2" type="submit" id="btn-suport">
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Contacts (contacts.php) - trei file dinauntru
 * ---------------------------------------------------------------------- */

function ContactsPanel() {
  const page = useQuery(companyContactsQuery)
  const [current, setCurrent] = useState('')

  useEffect(() => {
    if (!page.data || page.data.tabs.length === 0) return
    setCurrent((prev) => (prev !== '' && page.data.tabs.some((t) => t.id === prev) ? prev : page.data.tabs[0].id))
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const tabs = page.data.tabs
  if (tabs.length === 0) {
    return (
      <div id="content">
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> There is nothing here you may see.
        </div>
      </div>
    )
  }
  const open = tabs.find((t) => t.id === current) ?? tabs[0]

  return (
    <div id="content">
      <InnerTabs tabs={tabs} current={open.id} onPick={setCurrent} />
      <div className="tab-content">
        <RoleForm
          key={open.id}
          role={open.id}
          label={open.label}
          chosen={open.employeeId}
          employees={page.data.employees}
        />
      </div>
    </div>
  )
}

function RoleForm({
  role,
  label,
  chosen,
  employees,
}: {
  role: string
  label: string
  chosen: number
  employees: { id: number; label: string }[]
}) {
  const query = useQueryClient()
  const [value, setValue] = useState(chosen > 0 ? String(chosen) : '')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveContactRole(role, Number(value))
      .then(() => {
        toast(label + ' was saved!', 'Success')
        void query.invalidateQueries({ queryKey: ['settings', 'companies', 'contacts'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div className="tab-pane fade in active">
      <form id={'form-contact-' + role} method="post" onSubmit={submit}>
        <div className="col-md-6">
          <div className="form-group">
            <label className="col-sm-3 control-label">Employee:</label>
            <div className="col-sm-9">
              <select
                name="employee"
                className="form-control input-sm"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              >
                <option value="">Choose</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <button className="btn btn-success" type="submit">
              Save Changes
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Groups (groups.php + GroupModel din companies.js)
 * ---------------------------------------------------------------------- */

const GROUP_ORDER_NAMES = ['created', 'modified', 'name', '']

/** `GroupModel` din `public/js/settings/companies.js`, coloana cu coloana. */
function groupColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: 'Date created',
      titleHtml: '<i class="far fa-calendar"></i> Date created',
      orderable: true,
      visible: true,
      width: 150,
      className: 'td-small a-left',
      html: (row) => esc(whenText(row.created)),
    },
    {
      index: 1,
      title: 'Date modified',
      titleHtml: '<i class="far fa-calendar"></i> Date modified',
      orderable: true,
      visible: true,
      width: 150,
      className: 'td-small a-left',
      html: (row) => esc(whenText(row.modified)),
    },
    {
      index: 2,
      title: 'Name',
      orderable: true,
      visible: true,
      className: 'td-small a-left td-col-name',
      html: (row) => esc(String(row.name ?? '')),
    },
    {
      index: 3,
      title: 'Actions',
      orderable: false,
      visible: true,
      html: (row) => {
        const id = Number(row.id ?? 0)
        return (
          `<button class="btn btn-danger btn-xs pull-right od-remove-group" data-id="${id}">` +
          '<i class="fas fa-trash" aria-hidden="true"></i> Remove</button>' +
          `<button class="btn btn-primary btn-xs pull-right od-edit-group" style="margin-right:5px;" data-id="${id}" data-name="${esc(String(row.name ?? ''))}">` +
          '<i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>'
        )
      },
    },
  ]
}

function GroupsPanel() {
  const query = useQueryClient()
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null)
  const [state, setState] = useTableState(GROUPS_TABLE, {
    start: 0,
    /* `order: [[1, 'desc']]` din `GroupPage.initDatatable`. */
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => groupColumns(), [])
  const table = useQuery({
    ...companyGroupsQuery(tableRequest(state, GROUP_ORDER_NAMES, 'modified')),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'companies', 'groups'] })

  return (
    <div id="content">
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setEditing({ id: 0, name: '' })}>
        <i className="fas fa-plus" aria-hidden="true"></i> New group
      </button>
      <h4>
        <strong>Groups</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>groups</strong>
      </label>
      <hr />
      <div
        onClick={(event) => {
          const target = event.target as HTMLElement
          const edit = target.closest('.od-edit-group')
          if (edit) {
            setEditing({ id: Number(edit.getAttribute('data-id') ?? 0), name: edit.getAttribute('data-name') ?? '' })
            return
          }
          const remove = target.closest('.od-remove-group')
          if (!remove) return
          const id = Number(remove.getAttribute('data-id') ?? 0)
          if (id < 1 || !confirm('Are you sure? The group will be removed.')) return
          void removeGroup(id)
            .then(() => {
              toast('Group was removed', 'Success')
              reload()
            })
            .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
        }}
      >
        <DataTable
          id={GROUPS_TABLE}
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `rowd_${Number(row.id)}`)}
          emptyHint="A group is a set of devices or people you want to handle together."
        />
      </div>
      <GroupModal
        group={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          reload()
        }}
      />
    </div>
  )
}

/**
 * `new-group.php` si `edit-group.php` erau doua machete cu acelasi camp si
 * acelasi buton; aici sunt una singura, ca si capatul care le primeste.
 */
function GroupModal({
  group,
  onClose,
  onSaved,
}: {
  group: { id: number; name: string } | null
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')

  useEffect(() => {
    setName(group?.name ?? '')
  }, [group])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveGroup({ id: group?.id ?? 0, group_name: name })
      .then(() => {
        toast(group && group.id > 0 ? 'Group was modified!' : 'Group was saved!', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <Modal
      id="companyGroupModal"
      open={group !== null}
      onClose={onClose}
      title={group && group.id > 0 ? 'Edit group' : 'New group'}
      labelledBy="companyGroupModalLabel"
    >
      <form className="form-horizontal" method="post" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            Name <span className="required">*</span>
          </label>
          <div className="col-sm-9">
            <input
              type="text"
              className="form-control input-sm"
              name="group_name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-offset-3 col-sm-9 aright">
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
              Close
            </button>
            <button type="submit" className="btn btn-primary">
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Device Owners (owners.php + OwnersSupportModel din owners.js)
 * ---------------------------------------------------------------------- */

const OWNER_ORDER_NAMES = ['firstname', 'lastname', 'company_name', 'email', 'phone', 'mobile_phone', 'create_date', 'last_edit', '']

/**
 * `OwnersSupportModel` din `public/js/settings/owners.js`.
 *
 * Coloana "Created" din portal incerca sa scrie si "by Prenume Nume", dar
 * `ownersData` nu aducea nici `created_by`, nici `Prenume`, nici `Nume` - deci
 * randul acela nu s-a vazut niciodata. Capatul nou le aduce (aceeasi lista pe
 * care o serveste si ecranul Contacts), deci acum chiar se vede.
 */
function ownerColumns(): TableColumn[] {
  return [
    { index: 0, title: 'Firstname', orderable: true, visible: true, className: 'a-left', html: (row) => '<strong>' + esc(String(row.firstname ?? '')) + '</strong>' },
    { index: 1, title: 'Lastname', orderable: true, visible: true, className: 'a-left', html: (row) => '<strong>' + esc(String(row.lastname ?? '')) + '</strong>' },
    { index: 2, title: 'Company', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.companyName ?? '')) },
    { index: 3, title: 'Email', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.email ?? '')) },
    { index: 4, title: 'Phone', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.phone ?? '')) },
    { index: 5, title: 'Mobile Phone', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.mobilePhone ?? '')) },
    {
      index: 6,
      title: 'Created',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => {
        const by = String(row.createdBy ?? '')
        return esc(whenText(row.createDate)) + (by !== '' ? '<p style="color:#52bbb6">by ' + esc(by) + '</p>' : '')
      },
    },
    { index: 7, title: 'Last Edit', orderable: true, visible: true, className: 'a-left', html: (row) => esc(whenText(row.lastEdit)) },
    {
      index: 8,
      title: 'Actions',
      orderable: false,
      visible: true,
      html: (row) =>
        `<button class="btn btn-danger btn-xs pull-right od-remove-owner" data-id="${Number(row.id ?? 0)}">` +
        '<i class="fas fa-trash" aria-hidden="true"></i> Remove</button>',
    },
  ]
}

function OwnersPanel() {
  const query = useQueryClient()
  const [state, setState] = useTableState(OWNERS_TABLE, {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => ownerColumns(), [])
  const table = useQuery({
    ...companyOwnersQuery(tableRequest(state, OWNER_ORDER_NAMES, 'firstname')),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  return (
    <div id="content">
      <a className="pull-right btn btn-success btn-sm" title="Create new contact" href="/app/settings/contacts">
        <i className="fa fa-user-plus" aria-hidden="true"></i> Create
      </a>
      <h4>
        <strong>Owners</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>owners</strong>
      </label>
      <hr />
      {/*
        Crearea si editarea unui detinator sunt ACELASI formular ca in Settings
        -> Contacts, si acolo au si fost mutate (`/app/settings/contacts`). Aici
        ar fi fost al doilea exemplar al aceluiasi set de campuri - exact ce
        despartise deja modala de creare de formularul de editare in portal.
      */}
      <div
        onClick={(event) => {
          const remove = (event.target as HTMLElement).closest('.od-remove-owner')
          if (!remove) return
          const id = Number(remove.getAttribute('data-id') ?? 0)
          if (id < 1 || !confirm('Are you sure? The owner will be removed.')) return
          void removeOwner(id)
            .then(() => {
              toast('Owner was removed', 'Success')
              void query.invalidateQueries({ queryKey: ['settings', 'companies', 'owners'] })
            })
            .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
        }}
      >
        <DataTable
          id={OWNERS_TABLE}
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.id)}`)}
          emptyHint="An owner is the person a device belongs to."
        />
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Company Events (events.php + UserEventsModel)
 * ---------------------------------------------------------------------- */

const EVENT_ORDER_NAMES = ['event_date', 'event_message', 'user']

function eventColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: 'Date',
      titleHtml: '<i class="far fa-calendar"></i> Date',
      orderable: true,
      visible: true,
      width: 150,
      className: 'td-small a-left',
      html: (row) => esc(whenText(row.date)),
    },
    { index: 1, title: 'Message', orderable: true, visible: true, className: 'td-small a-left', html: (row) => esc(String(row.message ?? '')) },
    {
      index: 2,
      title: 'User',
      orderable: true,
      visible: true,
      className: 'td-small a-left',
      html: (row) => {
        const user = String(row.user ?? '')
        return user !== '' ? esc(user) : '<strong>System</strong>'
      },
    },
  ]
}

function EventsPanel() {
  const [state, setState] = useTableState(EVENTS_TABLE, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => eventColumns(), [])
  const table = useQuery({
    ...companyEventsQuery(tableRequest(state, EVENT_ORDER_NAMES, 'event_date')),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  return (
    <div id="content">
      <h4>
        <strong>Events</strong>
      </h4>
      <hr />
      <DataTable
        id={EVENTS_TABLE}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `rowd_${Number(row.id)}`)}
        emptyHint="Nothing has happened on this account yet."
      />
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Eligible for Adam Intop (adam_intop_companies.php)
 * ---------------------------------------------------------------------- */

function AdamIntopPanel() {
  const query = useQueryClient()
  const page = useQuery(adamIntopQuery)
  const [assigned, setAssigned] = useState(false)

  useEffect(() => {
    if (!page.data) return
    setAssigned(page.data.assigned)
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const editable = page.data.editable

  return (
    <div id="content">
      <h4>
        <strong>Adam Intop</strong> settings
      </h4>
      <hr />
      <form
        method="post"
        id="save-adam-intop-form-companies"
        onSubmit={(event) => {
          event.preventDefault()
          void saveAdamIntop(assigned)
            .then(() => {
              toast('Successfully saved.', 'Success')
              void query.invalidateQueries({ queryKey: ['settings', 'companies', 'adam-intop'] })
            })
            .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
        }}
      >
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label">
                <input
                  type="checkbox"
                  name="assign"
                  checked={assigned}
                  disabled={!editable}
                  onChange={(e) => setAssigned(e.target.checked)}
                />
                &nbsp; Eligible for Adam Intop
              </label>
            </div>
          </div>
        </div>
        <div className="clearfix"></div>
        <div className="divider"></div>
        <div className="row">
          <div className="clearfix"></div>
          <hr className="dotted" />
          <div className="col-md-12 center-block">
            <button type="submit" id="saveaisettings" className="btn btn-success center-block" disabled={!editable}>
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
